/**
 * 会话列表管理 Composable。
 * 负责加载、搜索、置顶、删除会话，并根据当前用户身份隔离会话数据。
 */
import { computed } from "vue";
import { useAnonymousUser } from "./useAnonymousUser";
import { useAuthUser } from "./useAuthUser";

export interface SessionInfo {
  id: string; // 会话 ID
  title: string; // 会话标题
  createdAt: number; // 创建时间戳（毫秒）
  messageCount: number; // 消息数量
  isPinned?: boolean; // 是否置顶
  isTitleAutoGenerated?: boolean; // 标题是否自动生成
}

interface LoadSessionListOptions {
  force?: boolean;
}

let loadSessionListPromise: Promise<void> | null = null; // 防止并发加载

export function useSessionList() {
  const { getAnonymousId } = useAnonymousUser();
  const { user, initialized, refreshUser } = useAuthUser();
  const currentUserId = computed(() => getAnonymousId());
  const sessionList = useState<SessionInfo[]>("session-list", () => []); // 存储会话列表的状态
  const isLoading = useState("session-list-loading", () => true); // 首屏先展示加载态，避免用户初始化期间误显示空状态
  const lastSessionListLoadedAt = useState("session-list-loaded-at", () => 0); // 最后一次加载时间 
  const lastLoadedUserId = useState("session-list-loaded-user-id", () => ""); // 最后一次加载的用户 ID

  /**
   * 加载会话列表
   * @param options
   * @returns
   */
  const loadSessionList = async (options: LoadSessionListOptions = {}) => {
    if (loadSessionListPromise) {
      await loadSessionListPromise;
      if (!options.force) return;
    }

    loadSessionListPromise = (async () => {
      isLoading.value = true;
      try {
        if (!initialized.value) {
          await refreshUser();
        }

        if (!currentUserId.value) return;
        if (
          !options.force &&
          lastSessionListLoadedAt.value > 0 &&
          lastLoadedUserId.value === currentUserId.value
        ) {
          return;
        }

        // 🔗调用接口:获取会话列表
        const userId = currentUserId.value;
        const query = user.value ? undefined : { userId };
        const response: any = await $fetch("/api/session/sessions", {
          method: "GET",
          query,
        });

        if (response.success && response.sessions) {
          sessionList.value = response.sessions;
          lastSessionListLoadedAt.value = Date.now();
          lastLoadedUserId.value = userId;
        }
      } catch (error) {
        console.error("❌ 加载会话列表失败:", error);
      } finally {
        isLoading.value = false;
        loadSessionListPromise = null;
      }
    })();

    return loadSessionListPromise;
  };

  const removeSessionFromList = (sessionId: string) => {
    sessionList.value = sessionList.value.filter((session) => session.id !== sessionId);
  };

  return {
    currentUserId,
    sessionList,
    isLoading,
    lastSessionListLoadedAt,
    loadSessionList,
    removeSessionFromList,
    refreshSessionList: () => loadSessionList({ force: true }),
  };
}
